import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Users, UserCheck, Nfc, DoorOpen, ClipboardList, MonitorPlay,
  ScrollText, Server, ChevronRight, Lock, ShieldAlert, Fingerprint, Wifi, WifiOff, CircleCheck, LockOpen,
  Plus, ScanLine, Search, Pencil, Trash2, Download, Settings2,
} from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link, navigate } from '../router.js'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Select, useToast, usePermissions, apiRead } from '../admin/primitives.js'
import { EN_TETES_EXPORT } from '../admin/telechargement.js'
import { useSaveBar } from '../shell/editing.js'
import { useConfirm } from '../shell/confirm.js'
import { LiveNumber } from '../soirees/lib.js'
import {
  useApi, useAccessCaps, ActionButton, runCommand, AccessModal, SortHeader, deverrouillerPorte,
  tr, RESULT_LABELS, METHOD_LABELS, timeShort, dayTime, dateTime, relativeDay, PersonAvatar,
} from './lib.js'
import { useMenuAcces } from './menu-acces.js'
import {
  cheminPassagesPorteSoiree, phraseNuitHistorique, nomFichierHistorique, messageExport, csvHistorique,
  type NuitHistorique, type EvenementHistorique,
} from './historique-modele.js'
import { SectionCameraPorte } from './CameraPorte.js'

/**
 * MODULE CONTRÔLE D'ACCÈS — ACCESS OPERABILITY V1. Toutes les données viennent
 * du miroir local (le navigateur ne joint jamais UniFi Access). L'état des
 * actions vient d'UNE SEULE source (/access/capabilities, resolver Mode sûr V2)
 * — plus aucun « Lecture seule » codé en dur (§4-6). Tout objet métier affiché
 * est navigable (§128-129).
 */

export { AccessPeoplePage } from './PeoplePage.js'
export { AccessPersonPage } from './PersonPage.js'
export { AccessCredentialsPage, AccessCredentialPage } from './CredentialsPage.js'

function ModeBadge({ mode, label }: { mode: string; label: string }) {
  const cls = mode === 'EDIT' || mode === 'AUTOMATION' ? 'ac-mode--edit' : mode === 'DISABLED' ? 'ac-mode--off' : 'ac-mode--ro'
  return <span className={`ac-mode ${cls}`}><Lock size={12} aria-hidden="true" />{label}</span>
}

function AccessShell({ title, subtitle, right, children, leaf }: { title: string; subtitle?: string; right?: ReactNode; children: ReactNode; leaf?: string }) {
  const meta = useShellMeta()
  return (
    <Shell active="security" meta={meta}>
      <main className="lc-page lc-stagger ac-page">
        <Trail {...(leaf ? { leaf } : {})} />
        <header className="ac-head">
          <div><h1 className="lc-title">{title}</h1>{subtitle ? <p className="lc-subtitle">{subtitle}</p> : null}</div>
          {right ? <div className="ac-head__right">{right}</div> : null}
        </header>
        {children}
      </main>
    </Shell>
  )
}

/* ═══════════════ VUE D'ENSEMBLE — POSTE DE COMMANDE ═══════════════ */

interface Passage { id: string; at: string | null; actorId: string | null; actor: string | null; door: string | null; method: string | null; result: string | null; kind: 'EMPLOYEE' | 'VISITOR' | 'ACCESS'; personId: string | null; linkedName: string | null; photoUrl: string | null }
interface Technical { id: string; at: string | null; type: string | null; door: string | null; device: string | null; message: string | null }
interface DoorSup { id: string; name: string | null; fullName: string | null; lock: string | null; position: 'OPEN' | 'CLOSED' | 'NOT_REPORTED'; mode: string; modeLabel: string; modeUntil: string | null; modeIndefinite: boolean; concern: { concern: boolean; reason: string | null }; lastPassage: { at: string | null; actor: string | null; method: string | null; result: string | null } | null; lastDenied: { at: string | null; actor: string | null } | null; deviceOnline: boolean | null; cameraUid: string | null }
interface WatchItem { kind: string; severity: 'INFO' | 'WARNING' | 'ERROR'; label: string; doorId?: string | null }
interface OperatorAction { id: string; at: string | null; actor: string | null; door: string | null; doorId: string | null; method: string | null; result: string | null; label: string }
interface Overview {
  people: { n: number; active: number }; visitorsActive: number
  credentials: { nfc: number; tp: number }; doors: { n: number; open: number }
  devices: { n: number; offline: number }; deniedIdentified: number
  tiles: { policyNames: string[]; scheduleCount: number; intercoms: { n: number; online: number }; lastPassageAt: string | null; doorsScheduled: number; doorsHeld: number }
  emergency: 'NORMAL' | 'LOCKDOWN' | 'EVACUATION'
  passages: Passage[]; technical: Technical[]; operator: OperatorAction[]; doorsSupervision: DoorSup[]; watch: WatchItem[]
  freshness: { mode: string; modeLabel: string; lastSync: string | null; stale: boolean }
}

/**
 * TUILE FONCTION (V2.1 §22-33) — L'UNIQUE système de tuiles du dashboard :
 * chaque tuile est À LA FOIS la navigation vers sa fonction ET son état réel
 * (compteur, décomposition, dernier événement). Grille volontairement non
 * monotone : tailles et contenus varient selon la fonction (span 2, chips…).
 */
function FnTile({ label, icon, href, value, foot, tint, span, hero, children }: {
  label: string; icon: ReactNode; href: string; value?: number
  foot?: ReactNode; tint?: string | undefined; span?: boolean; hero?: boolean; children?: ReactNode
}) {
  return (
    <Link href={href} className={`lc-card lc-card--compact ac-fn ${tint ? `lc-card--${tint}` : ''} ${span ? 'ac-fn--span' : ''} ${hero ? 'ac-fn--hero' : ''}`}>
      <span className="lc-card__edge" aria-hidden="true" />
      <span className="lc-card__label ac-fn__head">{icon}{label}<ChevronRight size={13} className="ac-fn__chev" aria-hidden="true" /></span>
      {value != null ? <LiveNumber value={value} className="lc-card__value ac-fn__value" /> : null}
      {children}
      {foot ? <span className="lc-card__desc ac-fn__foot">{foot}</span> : null}
    </Link>
  )
}

export function AccessOverviewPage() {
  const { data, error, reload } = useApi<Overview>('/api/v1/access/overview')
  const caps = useAccessCaps()
  const menu = useMenuAcces()
  // Après un déverrouillage depuis le menu : la même relecture que la fiche porte.
  const relire = { apres: () => { setTimeout(reload, 1600) } }
  return (
    <AccessShell
      title="Contrôle d’accès"
      subtitle="Le poste de commande des accès physiques du club — depuis Gestion, sans ouvrir UniFi Access."
      right={data ? (
        <div className="ac-hero-status">
          {/* « En direct » seulement si la synchronisation l'est (04/11/2026) : du
              21/10 au 04/11, ce bandeau l'affirmait sous une clé refusée. */}
          <span className={data.freshness.stale ? 'lc-dot lc-dot--warning' : 'lc-dot lc-dot--ok lc-dot--live'} aria-hidden="true" />
          <span className={`ac-hero-status__live${data.freshness.stale ? ' ac-hero-status__live--stale' : ''}`}>{data.freshness.stale ? 'Données anciennes' : 'En direct'}</span>
          <ModeBadge mode={data.freshness.mode} label={data.freshness.modeLabel} />
          <span className="ac-hero-status__fresh">{data.freshness.lastSync ? `màj ${timeShort(data.freshness.lastSync)}` : ''}</span>
        </div>
      ) : null}
    >
      {error ? <p className="lc-hint ac-err">{error}</p> : null}
      {data ? (
        <>
          {data.emergency !== 'NORMAL' ? (
            <Link href="/securite/controle-acces/urgence" className="ac-emergency-banner"><ShieldAlert size={18} aria-hidden="true" /> État d’urgence : {data.emergency === 'LOCKDOWN' ? 'CONFINEMENT' : 'ÉVACUATION'}</Link>
          ) : null}

          {/* GRILLE UNIQUE (V2.1 §22-33) — UN seul système de tuiles : chaque
              fonction = une tuile qui NAVIGUE et MONTRE son état réel. Plus de
              mini-nav + rang KPI dupliqué. Actions rapides intégrées en tuile. */}
          <section className="ac-fngrid lc-stagger" aria-label="Fonctions du contrôle d’accès">
            <FnTile hero span tint="violet" icon={<Users size={16} />} label="Personnes" value={data.people.active}
              foot={`actives · ${data.people.n} au total`} href="/securite/controle-acces/personnes?filtre=active" />
            <FnTile tint="cyan" icon={<Nfc size={16} />} label="Identifiants" value={data.credentials.nfc + data.credentials.tp}
              foot={`${data.credentials.nfc} NFC · ${data.credentials.tp} Touch Pass`} href="/securite/controle-acces/identifiants" />
            <FnTile tint={data.tiles.doorsHeld > 0 ? 'blue' : undefined} icon={<DoorOpen size={16} />} label="Portes" value={data.doors.n}
              foot={[
                data.tiles.doorsScheduled > 0 ? `${data.tiles.doorsScheduled} en ouverture planifiée` : null,
                data.tiles.doorsHeld > 0 ? `${data.tiles.doorsHeld} maintenue(s) déverrouillée(s)` : null,
              ].filter(Boolean).join(' · ') || 'toutes en règle normale'} href="/securite/controle-acces/portes" />
            <FnTile icon={<UserCheck size={16} />} label="Visiteurs" value={data.visitorsActive}
              foot={data.visitorsActive > 0 ? 'accès temporaires actifs' : 'aucun accès temporaire'} href="/securite/controle-acces/visiteurs?filtre=en-cours" />
            <FnTile span icon={<ClipboardList size={16} />} label="Règles & horaires" href="/securite/controle-acces/regles"
              foot={`${data.tiles.scheduleCount} horaire(s) d’accès`}>
              <span className="ac-fn__chips">{data.tiles.policyNames.map((n) => <span key={n} className="ac-chip">{n}</span>)}</span>
            </FnTile>
            <FnTile icon={<MonitorPlay size={16} />} label="Interphonie" value={data.tiles.intercoms.n}
              foot={data.tiles.intercoms.online === data.tiles.intercoms.n ? 'toutes en ligne' : `${data.tiles.intercoms.online} en ligne sur ${data.tiles.intercoms.n}`}
              tint={data.tiles.intercoms.online < data.tiles.intercoms.n ? 'amber' : undefined} href="/securite/controle-acces/interphonie" />
            <FnTile icon={<ScrollText size={16} />} label="Historique" tint={data.deniedIdentified > 0 ? 'amber' : undefined}
              foot={`${data.deniedIdentified} refus identifié(s) · 24 h`} href="/securite/controle-acces/historique?identifie=1">
              <span className="ac-fn__big">{data.tiles.lastPassageAt ? dayTime(data.tiles.lastPassageAt) : 'Aucun passage'}</span>
            </FnTile>
            <FnTile icon={<Server size={16} />} label="Appareils" value={data.devices.n} tint={data.devices.offline > 0 ? 'amber' : undefined}
              foot={data.devices.offline > 0 ? `${data.devices.offline} hors ligne` : 'tous en ligne'}
              href={`/securite/controle-acces/appareils${data.devices.offline > 0 ? '?etat=hors-ligne' : ''}`} />
            {/* ACTIONS RAPIDES intégrées à la grille (§32) — capacités réelles. */}
            <div className="lc-card lc-card--compact ac-fn ac-fn--span ac-fn--actions">
              <span className="lc-card__edge" aria-hidden="true" />
              <span className="lc-card__label ac-fn__head"><Plus size={16} />Actions rapides</span>
              <div className="ac-fn__actions">
                <ActionButton caps={caps} action="CREATE_USER" label="Ajouter une personne" icon={<Plus size={14} aria-hidden="true" />} onRun={() => navigate('/securite/controle-acces/personnes?creer=1')} />
                <ActionButton caps={caps} action="ACCESS_NFC_ENROLL" label="Enrôler un badge" icon={<ScanLine size={14} aria-hidden="true" />} onRun={() => navigate('/securite/controle-acces/identifiants?enroler=1')} />
                <ActionButton caps={caps} action="ACCESS_CREATE_VISITOR" label="Créer un visiteur" icon={<UserCheck size={14} aria-hidden="true" />} onRun={() => navigate('/securite/controle-acces/visiteurs?creer=1')} />
                <Link href="/securite/controle-acces/personnes" className="lc-btn lc-btn--ghost"><Search size={14} aria-hidden="true" /> Rechercher</Link>
              </div>
            </div>
          </section>

          {/* Corps : passages identifiés | supervision + à surveiller */}
          <div className="ac-cc-grid">
            <section className="lc-panel ac-passages">
              <header className="lc-panel__head">
                <h2 className="lc-section-title">Derniers passages identifiés</h2>
                <span className="lc-panel__spacer" />
                <Link href="/securite/controle-acces/historique?identifie=1" className="ac-seeall">Tout l’historique →</Link>
              </header>
              <div className="lc-panel__body lc-panel__body--flush">
                {data.passages.length === 0 ? (
                  <div className="ac-empty"><Fingerprint size={26} aria-hidden="true" /><p>Aucun passage identifié récemment.</p><span>Seuls les accès attribués à une personne apparaissent ici.</span></div>
                ) : (
                  <ul className="ac-pass-list">
                    {data.passages.map((p) => {
                      const denied = p.result === 'BLOCKED'
                      const row = (
                        <>
                          <PersonAvatar name={p.actor} photoUrl={p.photoUrl} visitor={p.kind === 'VISITOR'} />
                          <span className="ac-pass__id"><strong>{p.actor ?? 'Identité'}</strong><em>{p.kind === 'VISITOR' ? 'Visiteur' : p.personId ? 'Employé lié' : 'Personne Access'}</em></span>
                          <span className="ac-pass__door">{p.door ?? '—'}</span>
                          <span className="lc-pill lc-pill--idle ac-pass__method">{tr(METHOD_LABELS, p.method)}</span>
                          <span className={`lc-pill ${denied ? 'lc-pill--warning' : 'lc-pill--ok'}`}>{denied ? 'Refusé' : 'Accordé'}</span>
                          <span className="ac-pass__time">{dayTime(p.at)}</span>
                        </>
                      )
                      // Destination canonique : la fiche ACCESS de la personne (§19-20).
                      return p.actorId && p.kind !== 'VISITOR'
                        ? <li key={p.id}><Link href={`/securite/controle-acces/personnes/${p.actorId}`} className={`ac-pass ac-pass--link ${denied ? 'ac-pass--denied' : ''}`} {...menu.personne({ id: p.actorId, nom: p.actor, personId: p.personId, personName: p.linkedName })}>{row}</Link></li>
                        : <li key={p.id}>{p.kind === 'VISITOR' ? <Link href="/securite/controle-acces/visiteurs" className={`ac-pass ac-pass--link ${denied ? 'ac-pass--denied' : ''}`}>{row}</Link> : <span className={`ac-pass ${denied ? 'ac-pass--denied' : ''}`}>{row}</span>}</li>
                    })}
                  </ul>
                )}
              </div>
            </section>

            <div className="ac-cc-side">
              <section className="lc-panel">
                <header className="lc-panel__head"><h2 className="lc-section-title">État du site</h2></header>
                <div className="lc-panel__body ac-supervision">
                  {data.doorsSupervision.map((d) => (
                    <Link key={d.id} href={`/securite/controle-acces/portes/${d.id}`} className="ac-superdoor" {...menu.porte({ id: d.id, nom: d.name, camera: d.cameraUid }, relire)}>
                      <div className="ac-superdoor__top">
                        <span className={`ac-dotstate ${d.concern.concern ? 'is-open' : d.lock === 'lock' ? 'is-locked' : 'is-unlocked'}`} />
                        <strong>{d.name}</strong>
                        {d.deviceOnline === false ? <WifiOff size={13} className="ac-offline" aria-hidden="true" /> : null}
                      </div>
                      {/* Le POURQUOI d'abord (mode), puis verrou · position (V2.1 §8). */}
                      <div className="ac-superdoor__states">
                        <span className={`ac-doormode ac-doormode--sm ac-doormode--${MODE_CLASS[d.mode] ?? 'unknown'}`}>{d.modeLabel}</span>
                        <span>{d.lock === 'lock' ? 'Verrouillée' : 'Déverrouillée'}</span>
                        <span aria-hidden="true">·</span>
                        <span title={d.position === 'NOT_REPORTED' ? 'Position non remontée par UniFi Access — pas forcément fermée.' : undefined}>{d.position === 'OPEN' ? 'Ouverte' : d.position === 'CLOSED' ? 'Fermée' : 'Position n. r.'}</span>
                      </div>
                      {d.concern.concern ? <div className="ac-superdoor__concern">⚠ {d.concern.reason}</div> : null}
                      <div className="ac-superdoor__last">{d.lastPassage ? <>Dernier : {d.lastPassage.actor ?? '—'} · {dayTime(d.lastPassage.at)}</> : 'Aucun passage identifié'}</div>
                    </Link>
                  ))}
                </div>
              </section>

              <section className="lc-panel">
                <header className="lc-panel__head"><h2 className="lc-section-title">À surveiller</h2></header>
                <div className="lc-panel__body">
                  {data.watch.length === 0 ? (
                    <div className="ac-watch-ok"><CircleCheck size={16} aria-hidden="true" /> Rien à signaler.</div>
                  ) : (
                    <ul className="ac-watch-list">
                      {data.watch.map((w, i) => {
                        // Chaque anomalie mène à sa surface (§87).
                        const href = w.kind === 'DOOR_STATE' ? (w.doorId ? `/securite/controle-acces/portes/${w.doorId}` : '/securite/controle-acces/portes')
                          : w.kind === 'DEVICE_OFFLINE' ? '/securite/controle-acces/appareils?etat=hors-ligne'
                          : w.kind === 'DENIED' ? '/securite/controle-acces/historique?resultat=BLOCKED&identifie=1&periode=24h'
                          : w.kind === 'VISITOR_LATE' ? '/securite/controle-acces/visiteurs'
                          : w.kind === 'CARD_ORPHAN' ? '/securite/controle-acces/identifiants?filtre=unassigned'
                          : '/securite/controle-acces/historique'
                        return (
                          <li key={i}>
                            <Link href={href} className={`ac-watch-item ac-watch-item--${w.severity.toLowerCase()} ac-watch-item--link`}
                              {...(w.kind === 'DOOR_STATE' && w.doorId ? menu.porte({ id: w.doorId, nom: data.doorsSupervision.find((d) => d.id === w.doorId)?.name ?? null }, relire) : {})}>
                              <span className="ac-watch-item__dot" aria-hidden="true" />{w.label}<ChevronRight size={12} aria-hidden="true" />
                            </Link>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              </section>
            </div>
          </div>

          {/* ACTIVITÉ OPÉRATEUR (§42) — les ouvertures distantes, hors passages. */}
          {data.operator.length > 0 ? (
            <section className="lc-panel ac-technical">
              <header className="lc-panel__head"><h2 className="lc-section-title">Activité opérateur</h2><span className="lc-panel__spacer" /><span className="lc-hint">Ouvertures distantes — ce ne sont pas des passages</span></header>
              <div className="lc-panel__body lc-panel__body--flush">
                <ul className="ac-tech-list">
                  {data.operator.map((o) => (
                    <li key={o.id} className="ac-tech">
                      <span className="ac-tech__time">{dayTime(o.at)}</span>
                      {o.doorId ? <Link href={`/securite/controle-acces/portes/${o.doorId}`} className="ac-tech__door ac-tech__door--link" {...menu.porte({ id: o.doorId, nom: o.door }, relire)}>{o.door ?? '—'}</Link> : <span className="ac-tech__door">{o.door ?? '—'}</span>}
                      <span className="ac-tech__msg">{o.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ) : null}

          {/* Activité technique — séparée du flux humain (§130-131) */}
          <section className="lc-panel ac-technical">
            <header className="lc-panel__head"><h2 className="lc-section-title">Activité technique</h2><span className="lc-panel__spacer" /><span className="lc-hint">Portes, appareils et système — hors passages</span></header>
            <div className="lc-panel__body lc-panel__body--flush">
              <ul className="ac-tech-list">
                {data.technical.map((t) => (
                  <li key={t.id} className="ac-tech"><span className="ac-tech__time">{dayTime(t.at)}</span><span className="ac-tech__door">{t.door ?? t.device ?? 'Système'}</span><span className="ac-tech__msg">{t.message ?? t.type}</span></li>
                ))}
                {data.technical.length === 0 ? <li className="lc-hint" style={{ padding: 'var(--sp-3) var(--sp-6)' }}>Aucune activité technique récente.</li> : null}
              </ul>
            </div>
          </section>

          {data.freshness.stale ? <p className="lc-hint ac-stale">Données possiblement anciennes — dernière synchronisation {data.freshness.lastSync ? new Date(data.freshness.lastSync).toLocaleString('fr-FR') : 'inconnue'}.</p> : null}
        </>
      ) : (!error ? <div className="ac-empty"><p>Chargement…</p></div> : null)}
    </AccessShell>
  )
}

/* ═══════════════ VISITEURS (§13, filtre en cours + création réelle) ═══════════════ */

interface Visitor { id: string; firstName: string; lastName: string; company: string | null; reason: string | null; startTime: number | null; endTime: number | null; status: string; hasQr: boolean; inviter: string | null }
const VISITOR_STATUS: Record<string, string> = { UPCOMING: 'À venir', VISITING: 'En cours', VISITED: 'Terminée', NO_VISIT: 'Sans visite', CANCELLED: 'Annulée', ACTIVE: 'En cours' }

export function AccessVisitorsPage() {
  const caps = useAccessCaps()
  const toast = useToast()
  const confirm = useConfirm()
  const q0 = useMemo(() => new URLSearchParams(window.location.search), [])
  const [filter, setFilter] = useState(q0.get('filtre') ?? 'tous')
  const [createOpen, setCreateOpen] = useState(q0.get('creer') === '1')
  const [editing, setEditing] = useState<Visitor | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const { data, reload } = useApi<{ visitors: Visitor[] }>('/api/v1/access/visitors')
  useEffect(() => {
    window.history.replaceState(null, '', `/securite/controle-acces/visiteurs${filter !== 'tous' ? `?filtre=${filter}` : ''}`)
  }, [filter])
  const now = Date.now() / 1000
  const enCours = (v: Visitor) => !['VISITED', 'NO_VISIT', 'CANCELLED'].includes(v.status) && (v.endTime == null || v.endTime > now)
  const shown = (data?.visitors ?? []).filter((v) => filter !== 'en-cours' || enCours(v))

  const cancel = async (v: Visitor) => {
    if (!(await confirm({ title: `Annuler la visite de ${v.firstName} ${v.lastName} ?`, message: 'Ses accès temporaires seront révoqués dans UniFi Access.', variant: 'sensitive', confirmLabel: 'Annuler la visite' }))) return
    setBusy(v.id)
    const r = await runCommand('ACCESS_CANCEL_VISITOR', { visitor_id: v.id })
    setBusy(null)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'Visite annulée dans UniFi Access.')
    setTimeout(reload, 1600)
  }
  const giveQr = async (v: Visitor) => {
    if (!(await confirm({ title: `Attribuer un QR à ${v.firstName} ${v.lastName} ?`, message: 'UniFi Access génère un QR d’accès pour ce visiteur.', confirmLabel: 'Attribuer' }))) return
    setBusy(v.id)
    const r = await runCommand('ACCESS_VISITOR_QR', { visitor_id: v.id })
    setBusy(null)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'QR attribué dans UniFi Access.')
    setTimeout(reload, 1600)
  }

  return (
    <AccessShell title="Visiteurs" subtitle="Les invités et leurs accès temporaires."
      right={<ActionButton caps={caps} action="ACCESS_CREATE_VISITOR" label="Nouveau visiteur" icon={<Plus size={14} aria-hidden="true" />} onRun={() => setCreateOpen(true)} />}>
      <div className="ac-quickfilters" role="tablist" aria-label="Filtres visiteurs">
        {[['tous', 'Tous'], ['en-cours', 'Actifs / en cours']].map(([v, l]) => (
          <button key={v} type="button" role="tab" aria-selected={filter === v} className={`ac-qf ${filter === v ? 'is-on' : ''}`} onClick={() => setFilter(v!)}>{l}</button>
        ))}
      </div>
      <div className="ac-people-grid">
        {shown.map((v) => (
          <div key={v.id} className="ac-person-card">
            <div className="ac-person-card__id"><strong>{v.firstName} {v.lastName}</strong><span className={`ac-status ac-status--${v.status.toLowerCase()}`}>{tr(VISITOR_STATUS, v.status)}</span></div>
            <div className="ac-person-card__meta">
              {v.company ? <span>{v.company}</span> : null}
              <span>{v.reason}</span>
              <span>{v.startTime ? new Date(v.startTime * 1000).toLocaleString('fr-FR') : ''} → {v.endTime ? new Date(v.endTime * 1000).toLocaleString('fr-FR') : ''}</span>
              <span>Invité par {v.inviter ?? '—'}{v.hasQr ? ' · QR attribué' : ''}</span>
            </div>
            <div className="ac-visitor-actions">
              <ActionButton caps={caps} action="ACCESS_UPDATE_VISITOR" label="Modifier" busy={busy === v.id} onRun={() => setEditing(v)} />
              {!v.hasQr && enCours(v) ? <ActionButton caps={caps} action="ACCESS_VISITOR_QR" label="Attribuer un QR" busy={busy === v.id} onRun={() => void giveQr(v)} /> : null}
              {enCours(v) ? <ActionButton caps={caps} action="ACCESS_CANCEL_VISITOR" label="Annuler la visite" busy={busy === v.id} onRun={() => void cancel(v)} /> : null}
            </div>
          </div>
        ))}
        {data && shown.length === 0 ? <p className="lc-hint">Aucun visiteur{filter === 'en-cours' ? ' en cours' : ''}.</p> : null}
      </div>
      {createOpen ? <VisitorModal onClose={(changed) => { setCreateOpen(false); if (changed) { toast('ok', 'Visiteur créé dans UniFi Access.'); setTimeout(reload, 1600) } }} /> : null}
      {editing ? <VisitorModal visitor={editing} onClose={(changed) => { setEditing(null); if (changed) { toast('ok', 'Visiteur modifié dans UniFi Access.'); setTimeout(reload, 1600) } }} /> : null}
    </AccessShell>
  )
}

const toLocalInput = (ts: number | null): string => (ts ? new Date(ts * 1000).toISOString().slice(0, 16) : '')

function VisitorModal({ visitor, onClose }: { visitor?: Visitor; onClose: (changed: boolean) => void }) {
  const [v, setV] = useState({
    first: visitor?.firstName ?? '', last: visitor?.lastName ?? '', company: visitor?.company ?? '',
    reason: visitor?.reason ?? 'Interview', start: toLocalInput(visitor?.startTime ?? null), end: toLocalInput(visitor?.endTime ?? null),
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const valid = v.first && v.last && v.start && v.end
  const run = async () => {
    setBusy(true); setError(null)
    const body = {
      first_name: v.first, last_name: v.last, visitor_company: v.company || undefined, visit_reason: v.reason,
      start_time: Math.floor(new Date(v.start).getTime() / 1000), end_time: Math.floor(new Date(v.end).getTime() / 1000),
    }
    const r = visitor
      ? await runCommand('ACCESS_UPDATE_VISITOR', { visitor_id: visitor.id, visitor: body })
      : await runCommand('ACCESS_CREATE_VISITOR', { visitor: body })
    setBusy(false)
    if (!r.ok) { setError(r.error); return }
    onClose(true)
  }
  return (
    <AccessModal title={visitor ? `Modifier — ${visitor.firstName} ${visitor.lastName}` : 'Nouveau visiteur'} onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={!valid || busy} onClick={() => void run()}>{busy ? 'Enregistrement…' : visitor ? 'Enregistrer dans UniFi Access' : 'Créer dans UniFi Access'}</button></>
    }>
      <div className="ac-idform">
        <label className="ac-field">Prénom<input value={v.first} onChange={(e) => setV({ ...v, first: e.target.value })} /></label>
        <label className="ac-field">Nom<input value={v.last} onChange={(e) => setV({ ...v, last: e.target.value })} /></label>
        <label className="ac-field">Société (optionnel)<input value={v.company} onChange={(e) => setV({ ...v, company: e.target.value })} /></label>
        <label className="ac-field">Motif<input value={v.reason ?? ''} onChange={(e) => setV({ ...v, reason: e.target.value })} /></label>
        <label className="ac-field">Début<input type="datetime-local" value={v.start} onChange={(e) => setV({ ...v, start: e.target.value })} /></label>
        <label className="ac-field">Fin<input type="datetime-local" value={v.end} onChange={(e) => setV({ ...v, end: e.target.value })} /></label>
      </div>
      {error ? <p className="ac-err">{error}</p> : null}
    </AccessModal>
  )
}

/* ═══════════════ PORTES V2 (§87-89) — liste triable, alias Gestion ═══════════════ */

interface DoorRow { id: string; name: string; providerName: string | null; hasAlias: boolean; fullName: string; lock: string | null; position: 'OPEN' | 'CLOSED' | 'NOT_REPORTED'; type: string; mode: string; modeLabel: string; modeUntil: string | null; modeIndefinite: boolean; concern: { concern: boolean; reason: string | null }; lastPassage: { at: string | null; actor: string | null; result: string | null } | null; lastDenied: string | null; device: string | null; deviceOnline: boolean | null; deviceResolved: boolean; cameraUid: string | null }

export function AccessDoorsPage() {
  const q0 = useMemo(() => new URLSearchParams(window.location.search), [])
  const [sort, setSort] = useState(q0.get('tri') ?? 'name_asc')
  const [search, setSearch] = useState(q0.get('q') ?? '')
  const { data, reload } = useApi<{ doors: DoorRow[] }>('/api/v1/access/doors')
  const menu = useMenuAcces()
  const relire = { apres: () => { setTimeout(reload, 1600) } }
  useEffect(() => {
    const p = new URLSearchParams()
    if (sort !== 'name_asc') p.set('tri', sort)
    if (search) p.set('q', search)
    window.history.replaceState(null, '', `/securite/controle-acces/portes${p.size ? `?${p}` : ''}`)
  }, [sort, search])
  const s = search.trim().toLowerCase()
  const doors = (data?.doors ?? [])
    // Recherche : alias Gestion, nom UniFi, ID provider (§169).
    .filter((d) => !s || [d.name, d.providerName, d.fullName, d.id].some((v) => (v ?? '').toLowerCase().includes(s)))
    .sort((a, b) => {
      const dir = sort.endsWith('_desc') ? -1 : 1
      if (sort.startsWith('name')) return dir * a.name.localeCompare(b.name)
      if (sort.startsWith('mode')) return dir * a.modeLabel.localeCompare(b.modeLabel)
      if (sort.startsWith('state')) return dir * `${a.lock}/${a.position}`.localeCompare(`${b.lock}/${b.position}`)
      if (sort.startsWith('last')) return dir * ((a.lastPassage?.at ?? '').localeCompare(b.lastPassage?.at ?? ''))
      if (sort.startsWith('device')) return dir * ((a.device ?? '').localeCompare(b.device ?? ''))
      return a.name.localeCompare(b.name)
    })
  return (
    <AccessShell title="Portes & espaces" subtitle="Les portes du club — nom Gestion, état réel, dernier passage.">
      <div className="ac-toolbar">
        <label className="ac-searchwrap"><Search size={15} aria-hidden="true" />
          <input className="ac-search" placeholder="Alias Gestion, nom UniFi, ID…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Rechercher une porte" />
        </label>
      </div>
      <section className="lc-panel ac-plist-panel">
        <div className="lc-panel__body lc-panel__body--flush">
          <div className="ac-plist-head ac-doorshead" aria-hidden={undefined}>
            <SortHeader label="Porte" asc="name_asc" desc="name_desc" sort={sort} onSort={setSort} />
            <SortHeader label="Mode" asc="mode_asc" desc="mode_desc" sort={sort} onSort={setSort} />
            <SortHeader label="Verrou" asc="state_asc" desc="state_desc" sort={sort} onSort={setSort} />
            <span className="ac-sorth">Position</span>
            <SortHeader label="Appareil" asc="device_asc" desc="device_desc" sort={sort} onSort={setSort} />
            <SortHeader label="Dernier passage" asc="last_asc" desc="last_desc" sort={sort} onSort={setSort} />
            <span className="ac-sorth">Dernier refus</span>
          </div>
          <ul className="ac-plist">
            {doors.map((d) => (
              <li key={d.id}>
                <Link href={`/securite/controle-acces/portes/${d.id}`} className="ac-prow ac-doorrow" {...menu.porte({ id: d.id, nom: d.name, camera: d.cameraUid }, relire)}>
                  <span className="ac-prow__id"><DoorOpen size={16} aria-hidden="true" className="ac-credicon" />
                    <span className="ac-prow__name"><strong>{d.name}</strong><em>{d.hasAlias ? `UniFi : ${d.providerName}` : d.fullName}</em></span>
                  </span>
                  <span className="ac-doorrow__mode"><span className={`ac-doormode ac-doormode--${MODE_CLASS[d.mode] ?? 'unknown'}`} title={untilLabel(d) || undefined}>{d.modeLabel}</span>{d.concern.concern ? <span className="ac-doorrow__concern" title={d.concern.reason ?? ''}>⚠</span> : null}</span>
                  <span className={`ac-door-state ${d.lock === 'lock' ? 'ac-door-state--locked' : 'ac-door-state--unlocked'}`}>{d.lock === 'lock' ? 'Verrouillée' : 'Déverrouillée'}</span>
                  <span className={`ac-door-state ${d.position === 'OPEN' ? 'ac-door-state--open' : d.position === 'NOT_REPORTED' ? 'ac-door-state--na' : ''}`} title={d.position === 'NOT_REPORTED' ? 'UniFi Access ne remonte aucune position physique pour cette porte — cela ne signifie pas « fermée ».' : undefined}>{POSITION_UI[d.position]}</span>
                  <span className="ac-prow__owner">{d.deviceResolved ? <>{d.deviceOnline === false ? <WifiOff size={12} className="ac-offline" aria-hidden="true" /> : <Wifi size={12} className="ac-online" aria-hidden="true" />} {d.device}</> : <em className="ac-doorrow__nodev">Appareil non résolu</em>}</span>
                  <span className="ac-prow__last">{d.lastPassage ? (<><strong>{relativeDay(d.lastPassage.at)}</strong><em>{d.lastPassage.actor ?? ''}{d.lastPassage.result ? ` · ${tr(RESULT_LABELS, d.lastPassage.result)}` : ''}</em></>) : <em>Jamais</em>}</span>
                  <span className="ac-prow__denied">{d.lastDenied ? relativeDay(d.lastDenied) : '—'}</span>
                </Link>
              </li>
            ))}
          </ul>
          {data && doors.length === 0 ? <p className="lc-hint" style={{ padding: 'var(--sp-3) var(--sp-6)' }}>Aucune porte.</p> : null}
        </div>
      </section>
    </AccessShell>
  )
}

interface DoorFiche {
  id: string; name: string; alias: string | null; providerName: string | null; fullName: string
  /** Caméra de Sonde reliée (réglage local, uid seul). */
  camera: { uid: string; updatedAt: string } | null
  lock: string | null; position: 'OPEN' | 'CLOSED' | 'NOT_REPORTED'; type: string | null; floorId: string | null
  mode: string; modeLabel: string; modeUntil: string | null; modeIndefinite: boolean
  concern: { concern: boolean; reason: string | null }
  device: { name: string | null; online: boolean } | null
  devices: { id: string; name: string; online: boolean; isReader: boolean; type: string | null }[]
  policies: { id: string; name: string }[]
  events: { at: string | null; actor: string | null; actorId: string | null; method: string | null; result: string | null; classe: string }[]
  diagnostic: { providerId: string; rawLockState: string | null; rawPosition: string | null; rawLockRule: unknown; lockRuleFetchedAt: string | null; lastProviderUpdate: string | null; deviceAssociation: string }
}

const POSITION_UI: Record<string, string> = { OPEN: 'Ouverte', CLOSED: 'Fermée', NOT_REPORTED: 'Position non remontée' }
const MODE_CLASS: Record<string, string> = { NORMAL: 'normal', SCHEDULE: 'schedule', KEEP_UNLOCK: 'held', KEEP_LOCK: 'heldlock', CUSTOM: 'held', LOCK_EARLY: 'early', UNKNOWN: 'unknown' }
const untilLabel = (d: { modeUntil: string | null; modeIndefinite: boolean }): string =>
  d.modeIndefinite ? 'jusqu’à annulation' : d.modeUntil ? `jusqu’à ${new Date(d.modeUntil).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : ''

export function AccessDoorPage({ id }: { id: string }) {
  const { data, reload } = useApi<DoorFiche>(`/api/v1/access/doors/${id}`)
  const caps = useAccessCaps()
  const toast = useToast()
  const confirm = useConfirm()
  const meta = useShellMeta()
  const perms = usePermissions()
  const [busy, setBusy] = useState<string | null>(null)
  const [overrideOpen, setOverrideOpen] = useState(false)
  const menu = useMenuAcces()

  /* ─── ALIAS GESTION (§77-84, §172) et CAMÉRA DE LA PORTE (24/10/2026) —
     mutations LOCALES, auditées, sous UNE Save Bar : editing.tsx n'admet
     qu'une unité d'édition par page. Les effets suivent la VALEUR serveur,
     pas l'objet relu : la relecture 1,6 s après un déverrouillage, ou un
     enregistrement partiel, n'efface plus une saisie en cours. ─── */
  const [alias, setAlias] = useState<string | null>(null)
  const aliasServeur = data ? (data.alias ?? '') : undefined
  useEffect(() => { if (aliasServeur !== undefined) setAlias(aliasServeur) }, [aliasServeur])
  const canAlias = !perms.loading && perms.permissions.includes('access.doors.manage')
  const peutSonde = !perms.loading && perms.permissions.includes('sonde.read')
  // Comparé sans les espaces de bord, que le serveur retire : « Bar » suivi d'une espace n'est pas une modification.
  const aliasDirty = !!(canAlias && data && alias !== null && alias.trim() !== (data.alias ?? ''))
  const cameraServeur = data ? (data.camera?.uid ?? null) : undefined
  const [camera, setCamera] = useState<string | null | undefined>(undefined)
  useEffect(() => { setCamera(cameraServeur) }, [cameraServeur])
  // Poser une caméra demande sonde.read ; la retirer, seulement Gérer les portes.
  const cameraDirty = canAlias && cameraServeur !== undefined && camera !== undefined && camera !== cameraServeur
    && (camera === null || peutSonde)
  useSaveBar({
    dirty: aliasDirty || cameraDirty,
    count: (aliasDirty ? 1 : 0) + (cameraDirty ? 1 : 0),
    saveLabel: aliasDirty && cameraDirty ? 'Enregistrer' : cameraDirty ? 'Enregistrer la caméra' : 'Enregistrer le nom Gestion',
    onSave: async () => {
      const { adminMutate } = await import('../admin/primitives.js')
      // La caméra d'abord : si Sonde la refuse, rien d'autre n'est tenté.
      if (cameraDirty) {
        const c = await adminMutate<{ error?: string; message?: string }>('PUT', `/api/v1/access/doors/${id}/camera`, { cameraUid: camera ?? null })
        if (c.status >= 400) return c.data.message ?? 'Refusé.'
      }
      if (aliasDirty) {
        const nom = alias?.trim() ?? ''
        const r = await adminMutate<{ error?: string; message?: string; duplicate?: boolean }>('PUT', `/api/v1/access/doors/${id}/alias`, { displayName: nom || null })
        if (r.status >= 400) {
          if (cameraDirty) reload()
          return r.data.message ?? 'Refusé.'
        }
        // Le nom tel que le serveur le garde (rogné) : la saisie avec ses espaces ne reste pas dans le champ.
        setAlias(nom)
        if (r.data.duplicate) toast('error', 'Attention : une autre porte utilise déjà ce nom — cela peut prêter à confusion.')
      }
      reload()
      return true
    },
    onCancel: () => { if (data) { setAlias(data.alias ?? ''); setCamera(data.camera?.uid ?? null) } },
    successMessage: aliasDirty && cameraDirty ? 'Nom Gestion et caméra enregistrés.'
      : cameraDirty ? (camera === null ? 'Caméra retirée de la porte.' : 'Caméra de la porte enregistrée.')
      : 'Nom Gestion enregistré (le nom UniFi n’a pas changé).',
  })

  // Le geste vit dans lib.tsx, partagé avec le menu clic droit : même
  // confirmation critique, même commande, même toast (une implémentation).
  const suiviDeverrouillage = {
    enCours: (oui: boolean) => setBusy(oui ? 'unlock' : null),
    apres: () => { setTimeout(reload, 1600) },
  }
  const unlock = async () => {
    if (!data) return
    if (await deverrouillerPorte(data.id, { confirm, toast, enCours: suiviDeverrouillage.enCours })) suiviDeverrouillage.apres()
  }

  return (
    <Shell active="security" meta={meta}>
      <main className="lc-page lc-stagger ac-page">
        <SmartBack fallback="/securite/controle-acces/portes" />
        {data ? (<>
          <header className="ac-head" {...menu.porte({ id: data.id, nom: data.name, camera: data.camera?.uid ?? null }, suiviDeverrouillage)}><div><h1 className="lc-title">{data.name}</h1><p className="lc-subtitle">{data.alias ? <>UniFi Access : <span className="ac-mono">{data.providerName}</span> · </> : null}{data.fullName}</p></div>
            <div className="ac-actions">
              <ActionButton caps={caps} action="ACCESS_DOOR_UNLOCK" label="Déverrouiller" icon={<LockOpen size={14} aria-hidden="true" />} busy={busy === 'unlock'} onRun={() => void unlock()} />
              <ActionButton caps={caps} action="ACCESS_DOOR_OVERRIDE" label="Forcer l’état" icon={<Settings2 size={14} aria-hidden="true" />} onRun={() => setOverrideOpen(true)} />
            </div>
          </header>
          {/* QUATRE NOTIONS SÉPARÉES (V2.1 §5-8) : mode · verrou · position · appareil. */}
          <div className="ac-door-states ac-door-states--lg">
            <span className={`ac-doormode ac-doormode--${MODE_CLASS[data.mode] ?? 'unknown'}`}>{data.modeLabel}{untilLabel(data) ? ` · ${untilLabel(data)}` : ''}</span>
            <span className={`ac-door-state ${data.lock === 'lock' ? 'ac-door-state--locked' : 'ac-door-state--unlocked'}`}>{data.lock === 'lock' ? 'Verrouillée' : 'Déverrouillée'}</span>
            <span className={`ac-door-state ${data.position === 'OPEN' ? 'ac-door-state--open' : ''}`} title={data.position === 'NOT_REPORTED' ? 'UniFi Access ne remonte actuellement aucune position physique pour cette porte.' : undefined}>{POSITION_UI[data.position]}</span>
            {data.device ? <span className="ac-door-state">{data.device.online ? <Wifi size={12} aria-hidden="true" /> : <WifiOff size={12} aria-hidden="true" />} {data.device.name}</span> : <span className="ac-door-state">Appareil non résolu</span>}
          </div>
          {data.concern.concern ? <p className="ac-blockhint">⚠ {data.concern.reason}</p> : null}
          <div className="ac-detail-grid">
            <section className="ac-panel">
              <h2 className="lc-section-title">Identité</h2>
              {canAlias ? (
                <div className="ac-idform ac-idform--door">
                  <label className="ac-field">Nom affiché dans Gestion
                    <input value={alias ?? ''} onChange={(e) => setAlias(e.target.value)} placeholder={data.providerName ?? ''} />
                  </label>
                  <label className="ac-field">Nom UniFi Access
                    <input value={data.providerName ?? ''} disabled title="Le renommage de porte n’est pas exposé par l’API officielle UniFi Access." />
                  </label>
                  <p className="lc-hint">Le nom Gestion est uniquement utilisé dans Gestion (vide = nom UniFi). Le nom UniFi est celui du contrôleur — son renommage n’est pas exposé par l’API officielle.</p>
                </div>
              ) : (
                <dl className="ac-dl">
                  <div><dt>Nom Gestion</dt><dd>{data.alias ?? '— (nom UniFi)'}</dd></div>
                  <div><dt>Nom UniFi Access</dt><dd className="ac-mono">{data.providerName}</dd></div>
                </dl>
              )}
              <dl className="ac-dl">
                <div><dt>ID provider</dt><dd className="ac-mono">{data.id}</dd></div>
                <div><dt>Type</dt><dd>{data.type ?? '—'}</dd></div>
              </dl>
            </section>
            <SectionCameraPorte lien={data.camera} valeur={camera ?? null} onChange={setCamera} peutGerer={canAlias} peutSonde={peutSonde} />
            <section className="ac-panel">
              <h2 className="lc-section-title">Règles couvrant cette porte</h2>
              <div className="ac-chips">{data.policies.map((p) => <Link key={p.id} href={`/securite/controle-acces/regles/${p.id}`} className="ac-chip ac-chip--link">{p.name}</Link>)}{data.policies.length === 0 ? <span className="lc-hint">Aucune.</span> : null}</div>
              <p className="lc-hint"><Link href={`/securite/controle-acces/historique?porte=${data.id}`} className="ac-seeall">Tout l’historique de cette porte →</Link></p>
            </section>
            <section className="ac-panel">
              <h2 className="lc-section-title">Diagnostic API</h2>
              <dl className="ac-dl">
                <div><dt>Provider ID</dt><dd className="ac-mono">{data.diagnostic.providerId}</dd></div>
                <div><dt>Verrou (brut)</dt><dd className="ac-mono">{data.diagnostic.rawLockState ?? 'null'}</dd></div>
                <div><dt>Position (brut)</dt><dd className="ac-mono">{data.diagnostic.rawPosition ?? 'null (non remontée)'}</dd></div>
                <div><dt>lock_rule (brut)</dt><dd className="ac-mono">{JSON.stringify(data.diagnostic.rawLockRule)}</dd></div>
                <div><dt>Règle lue le</dt><dd>{data.diagnostic.lockRuleFetchedAt ? new Date(data.diagnostic.lockRuleFetchedAt).toLocaleString('fr-FR') : '—'}</dd></div>
                <div><dt>Dernière sync provider</dt><dd>{data.diagnostic.lastProviderUpdate ? new Date(data.diagnostic.lastProviderUpdate).toLocaleString('fr-FR') : '—'}</dd></div>
                <div><dt>Appareils (location_id)</dt><dd>{data.diagnostic.deviceAssociation}</dd></div>
              </dl>
              <p className="lc-hint">Ce que le provider RETOURNE, sans interprétation — « API ou bug Gestion » se tranche ici.</p>
            </section>
            <section className="ac-panel ac-panel--wide"><h2 className="lc-section-title">Derniers événements</h2>
              <ul className="ac-events">{data.events.map((e, i) => (
                <li key={i} className={`ac-event ${e.result && e.result !== 'ACCESS' && e.result !== 'SUCCESS' ? 'ac-event--denied' : ''}`}>
                  <span className="ac-event__time">{e.at ? new Date(e.at).toLocaleString('fr-FR') : '—'}</span>
                  <span className="ac-event__actor">{e.actorId ? <Link href={`/securite/controle-acces/personnes/${e.actorId}`} className="ac-eventlink" {...menu.personne({ id: e.actorId, nom: e.actor })}>{e.actor}</Link> : (e.actor ?? '—')}</span>
                  <span className="ac-event__method">{e.classe === 'REMOTE_OPERATOR_ACTION' ? 'Ouverture distante' : tr(METHOD_LABELS, e.method)}</span>
                  <span className="ac-event__result">{tr(RESULT_LABELS, e.result)}</span>
                </li>
              ))}</ul>
              <p className="lc-hint"><Link href={cheminPassagesPorteSoiree(data.id)} className="ac-seeall">Passages de la nuit en cours →</Link></p>
            </section>
          </div>
          {overrideOpen ? <DoorOverrideModal door={data} onClose={(changed) => { setOverrideOpen(false); if (changed) setTimeout(reload, 1600) }} /> : null}
        </>) : <p className="lc-hint">Chargement…</p>}
      </main>
    </Shell>
  )
}

/** FORCER L'ÉTAT (§95) — la règle temporaire officielle (7.10) : maintenir
 *  déverrouillée/verrouillée, durée personnalisée, retour à la normale, fin
 *  d'horaire anticipée. Action critique : confirmation explicite + step-up. */
function DoorOverrideModal({ door, onClose }: { door: DoorFiche; onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const confirm = useConfirm()
  const [rule, setRule] = useState('keep_unlock')
  const [minutes, setMinutes] = useState('60')
  const [busy, setBusy] = useState(false)
  const RULES = [
    { value: 'keep_unlock', label: 'Maintenir DÉVERROUILLÉE (jusqu’à annulation)' },
    { value: 'keep_lock', label: 'Maintenir VERROUILLÉE (jusqu’à annulation)' },
    { value: 'custom', label: 'Déverrouiller pour une durée précise' },
    { value: 'lock_early', label: 'Terminer l’horaire de déverrouillage en avance' },
    { value: 'reset', label: 'Rétablir la règle normale (annuler l’override)' },
  ]
  const run = async () => {
    const label = RULES.find((r) => r.value === rule)!.label
    if (!(await confirm({ title: `Forcer l’état de ${door.name} ?`, message: `${label}. L’état RÉEL de la porte va changer immédiatement.`, variant: 'critical', confirmLabel: 'Appliquer' }))) return
    setBusy(true)
    const payload: Record<string, unknown> = { door_id: door.id, rule }
    if (rule === 'custom') payload.interval = Number(minutes) || 60
    const r = await runCommand('ACCESS_DOOR_OVERRIDE', payload)
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', `« ${door.name} » : ${label.toLowerCase()} — appliqué par UniFi Access.`)
    onClose(true)
  }
  return (
    <AccessModal title={`Forcer l’état — ${door.name}`} onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={busy} onClick={() => void run()}>{busy ? 'Application…' : 'Appliquer'}</button></>
    }>
      <p className="lc-hint">Règle temporaire officielle UniFi Access — elle prime sur l’horaire jusqu’à son annulation. Action critique : la porte change d’état physiquement.</p>
      <div className="ac-radioset">
        {RULES.map((r) => (
          <label key={r.value} className={`ac-radiorow ${rule === r.value ? 'is-on' : ''}`}>
            <input type="radio" name="door-rule" checked={rule === r.value} onChange={() => setRule(r.value)} />
            {r.label}
          </label>
        ))}
      </div>
      {rule === 'custom' ? <label className="ac-field">Durée (minutes)<input type="number" min={1} max={1440} value={minutes} onChange={(e) => setMinutes(e.target.value)} /></label> : null}
    </AccessModal>
  )
}

/* ═══════ RÈGLES & HORAIRES (V2.1 §59-74) — DEUX concepts, UNE surface ═══════
 * · RÈGLE D'ACCÈS (access policy) : qui a le droit d'entrer, où, quand — avec
 *   son HORAIRE D'ACCÈS (schedule) réutilisable.
 * · PROGRAMMATION DE PORTE (door unlock schedule) : la porte se DÉVERROUILLE
 *   toute seule sur une plage — personne n'a besoin de badge. Concept distinct,
 *   géré côté console (non exposé par l'OpenAPI — dit honnêtement, §71). */

function RulesTabs({ active }: { active: 'regles' | 'horaires' }) {
  return (
    <>
      <div className="ac-quickfilters" role="tablist" aria-label="Règles & horaires">
        <Link href="/securite/controle-acces/regles" role="tab" aria-selected={active === 'regles'} className={`ac-qf ${active === 'regles' ? 'is-on' : ''}`}>Règles d’accès</Link>
        <Link href="/securite/controle-acces/horaires" role="tab" aria-selected={active === 'horaires'} className={`ac-qf ${active === 'horaires' ? 'is-on' : ''}`}>Horaires & jours fériés</Link>
      </div>
      <p className="lc-hint ac-rules-explain">
        Une <strong>règle d’accès</strong> décide qui peut ouvrir quelles portes, pendant son <strong>horaire d’accès</strong>.
        C’est différent de la <strong>programmation d’une porte</strong> (déverrouillage automatique sur une plage, sans badge) — celle-ci se lit sur la fiche de chaque porte (mode « Ouverture planifiée »).
      </p>
    </>
  )
}

export function AccessPoliciesPage() {
  const caps = useAccessCaps()
  const [editorOpen, setEditorOpen] = useState(false)
  const { data, reload } = useApi<{ policies: { id: string; name: string; scheduleId: string | null; scheduleName: string | null; doorCount: number; userCount: number }[] }>('/api/v1/access/policies')
  return (
    <AccessShell title="Règles & horaires" subtitle="Qui peut aller où, et quand."
      right={<ActionButton caps={caps} action="ACCESS_POLICY_WRITE" label="Nouvelle règle" icon={<Plus size={14} aria-hidden="true" />} onRun={() => setEditorOpen(true)} />}>
      <RulesTabs active="regles" />
      <div className="ac-policy-grid">
        {(data?.policies ?? []).map((p) => (
          <Link key={p.id} href={`/securite/controle-acces/regles/${p.id}`} className="ac-policy-card">
            <strong>{p.name}</strong>
            <div className="ac-policy-meta"><span>{p.userCount} personne(s)</span><span>{p.doorCount} porte(s)</span><span>{p.scheduleName ?? 'Horaire —'}</span></div>
            <ChevronRight size={16} aria-hidden="true" />
          </Link>
        ))}
        {data && data.policies.length === 0 ? <p className="lc-hint">Aucune règle.</p> : null}
      </div>
      {editorOpen ? <PolicyEditorModal onClose={(changed) => { setEditorOpen(false); if (changed) setTimeout(reload, 1600) }} /> : null}
    </AccessShell>
  )
}

interface PolicyFiche { id: string; name: string; scheduleId: string | null; resources: { id: string; type: string }[]; who: { users: { id: string; name: string | null }[]; userCount: number }; where: { doors: { id: string; name: string }[] }; when: { id: string; name: string | null; weekly: Record<string, { start_time: string; end_time: string }[]> | null } | null }

export function AccessPolicyPage({ id }: { id: string }) {
  const { data, reload } = useApi<PolicyFiche>(`/api/v1/access/policies/${id}`)
  const caps = useAccessCaps()
  const toast = useToast()
  const confirm = useConfirm()
  const meta = useShellMeta()
  const [editorOpen, setEditorOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const menu = useMenuAcces()
  const JOURS: [string, string][] = [['monday', 'Lun'], ['tuesday', 'Mar'], ['wednesday', 'Mer'], ['thursday', 'Jeu'], ['friday', 'Ven'], ['saturday', 'Sam'], ['sunday', 'Dim']]

  const remove = async () => {
    if (!data) return
    if (!(await confirm({ title: `Supprimer la règle « ${data.name} » ?`, message: `${data.who.userCount} personne(s) et ${data.where.doors.length} porte(s) sont concernées. Cette action est irréversible.`, variant: 'critical', confirmLabel: 'Supprimer' }))) return
    setBusy(true)
    const r = await runCommand('ACCESS_POLICY_WRITE', { policy_id: data.id, delete: true })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'Règle supprimée dans UniFi Access.')
    navigate('/securite/controle-acces/regles')
  }

  return (
    <Shell active="security" meta={meta}>
      <main className="lc-page lc-stagger ac-page">
        <SmartBack fallback="/securite/controle-acces/regles" />
        {data ? (<>
          <header className="ac-head"><div><h1 className="lc-title">{data.name}</h1></div>
            <div className="ac-actions">
              <ActionButton caps={caps} action="ACCESS_POLICY_WRITE" label="Modifier" icon={<Pencil size={14} aria-hidden="true" />} onRun={() => setEditorOpen(true)} />
              <ActionButton caps={caps} action="ACCESS_POLICY_WRITE" label="Supprimer" icon={<Trash2 size={14} aria-hidden="true" />} busy={busy} onRun={() => void remove()} />
            </div>
          </header>
          <div className="ac-qww">
            <section className="ac-panel"><h2 className="lc-section-title">QUI</h2><p className="ac-qww__count">{data.who.userCount} personne(s)</p>
              <div className="ac-chips">{data.who.users.slice(0, 40).map((u) => <Link key={u.id} href={`/securite/controle-acces/personnes/${u.id}`} className="ac-chip ac-chip--link" {...menu.personne({ id: u.id, nom: u.name })}>{u.name}</Link>)}</div>
            </section>
            <section className="ac-panel"><h2 className="lc-section-title">OÙ</h2><div className="ac-chips">{data.where.doors.map((d) => <Link key={d.id} href={`/securite/controle-acces/portes/${d.id}`} className="ac-chip ac-chip--door ac-chip--link" {...menu.porte({ id: d.id, nom: d.name })}>{d.name}</Link>)}</div></section>
            <section className="ac-panel"><h2 className="lc-section-title">QUAND</h2>
              {data.when ? (<><p className="ac-qww__sched"><Link href="/securite/controle-acces/horaires" className="ac-eventlink">{data.when.name}</Link></p>
                <div className="ac-week">{JOURS.map(([k, lbl]) => <div key={k} className="ac-week__day"><span className="ac-week__lbl">{lbl}</span>{(data.when!.weekly?.[k] ?? []).map((s, i) => <span key={i} className="ac-week__slot">{s.start_time}–{s.end_time}</span>)}</div>)}</div></>) : <p className="lc-hint">Aucun horaire.</p>}
            </section>
          </div>
          {editorOpen ? <PolicyEditorModal policy={data} onClose={(changed) => { setEditorOpen(false); if (changed) setTimeout(reload, 1600) }} /> : null}
        </>) : <p className="lc-hint">Chargement…</p>}
      </main>
    </Shell>
  )
}

/** ÉDITEUR DE RÈGLE (§112) — nom, portes, horaire. POST/PUT officiels. */
function PolicyEditorModal({ policy, onClose }: { policy?: PolicyFiche; onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const { data: doorsData } = useApi<{ doors: { id: string; name: string }[] }>('/api/v1/access/doors')
  const { data: schedData } = useApi<{ schedules: { id: string; name: string }[] }>('/api/v1/access/schedules')
  const [name, setName] = useState(policy?.name ?? '')
  const [scheduleId, setScheduleId] = useState(policy?.scheduleId ?? '')
  const [doorIds, setDoorIds] = useState<Set<string>>(new Set((policy?.resources ?? []).filter((r) => r.type === 'door').map((r) => r.id)))
  const [busy, setBusy] = useState(false)
  const toggle = (id: string) => { const n = new Set(doorIds); if (n.has(id)) n.delete(id); else n.add(id); setDoorIds(n) }
  const save = async () => {
    setBusy(true)
    // Format officiel (§5.2) : clé `resource`, types door/door_group conservés.
    const keptGroups = (policy?.resources ?? []).filter((r) => r.type !== 'door')
    const body = { name, resource: [...keptGroups, ...[...doorIds].map((dId) => ({ id: dId, type: 'door' }))], schedule_id: scheduleId || undefined }
    const r = await runCommand('ACCESS_POLICY_WRITE', policy ? { policy_id: policy.id, policy: body } : { policy: body })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', policy ? 'Règle modifiée dans UniFi Access.' : 'Règle créée dans UniFi Access.')
    onClose(true)
  }
  return (
    <AccessModal title={policy ? `Modifier — ${policy.name}` : 'Nouvelle règle d’accès'} onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={!name || doorIds.size === 0 || busy} onClick={() => void save()}>{busy ? 'Enregistrement…' : 'Enregistrer dans UniFi Access'}</button></>
    }>
      <label className="ac-field">Nom de la règle<input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. Équipe bar — soirs" /></label>
      <h3 className="ac-subtitle">Portes couvertes</h3>
      <div className="ac-checkset">
        {(doorsData?.doors ?? []).map((d) => (
          <label key={d.id} className={`ac-radiorow ${doorIds.has(d.id) ? 'is-on' : ''}`}>
            <input type="checkbox" checked={doorIds.has(d.id)} onChange={() => toggle(d.id)} />{d.name}
          </label>
        ))}
      </div>
      <h3 className="ac-subtitle">Horaire</h3>
      <Select value={scheduleId} options={(schedData?.schedules ?? []).map((s) => ({ value: s.id, label: s.name }))} onChange={setScheduleId} placeholder="Choisir un horaire…" ariaLabel="Horaire de la règle" />
      {/* L'horaire est un OBJET navigable, pas une chaîne morte (§68). */}
      <p className="lc-hint"><Link href="/securite/controle-acces/horaires" className="ac-seeall">Voir et modifier les horaires →</Link></p>
    </AccessModal>
  )
}

/* ═══════════════ HORAIRES V2 (§113-114) — éditeur humain CRUD ═══════════════ */

interface SchedRow { id: string; name: string; type: string; isDefault: boolean; weekly: Record<string, { start_time: string; end_time: string }[]> | null; holidayGroup: string | null; usedBy: number }
const JOURS_FULL: [string, string][] = [['monday', 'Lundi'], ['tuesday', 'Mardi'], ['wednesday', 'Mercredi'], ['thursday', 'Jeudi'], ['friday', 'Vendredi'], ['saturday', 'Samedi'], ['sunday', 'Dimanche']]

export function AccessSchedulesPage() {
  const caps = useAccessCaps()
  const toast = useToast()
  const confirm = useConfirm()
  const [editing, setEditing] = useState<SchedRow | 'new' | null>(null)
  const [holidayOpen, setHolidayOpen] = useState(false)
  const { data, reload } = useApi<{ schedules: SchedRow[]; holidayGroups: { id: string; name: string; description: string | null; days: number }[] }>('/api/v1/access/schedules')
  const JOURS: [string, string][] = [['monday', 'Lun'], ['tuesday', 'Mar'], ['wednesday', 'Mer'], ['thursday', 'Jeu'], ['friday', 'Ven'], ['saturday', 'Sam'], ['sunday', 'Dim']]

  const removeSchedule = async (s: SchedRow) => {
    if (!(await confirm({ title: `Supprimer l’horaire « ${s.name} » ?`, message: 'Les règles qui l’utilisent en seront privées.', variant: 'sensitive', confirmLabel: 'Supprimer' }))) return
    const r = await runCommand('ACCESS_SCHEDULE_WRITE', { schedule_id: s.id, delete: true })
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'Horaire supprimé dans UniFi Access.')
    setTimeout(reload, 1600)
  }

  return (
    <AccessShell title="Règles & horaires" subtitle="Les plages hebdomadaires et les jours fériés utilisés par les règles."
      right={<ActionButton caps={caps} action="ACCESS_SCHEDULE_WRITE" label="Nouvel horaire" icon={<Plus size={14} aria-hidden="true" />} onRun={() => setEditing('new')} />}>
      <RulesTabs active="horaires" />
      <section className="ac-panel"><h2 className="lc-section-title">Horaires d’accès</h2>
        {(data?.schedules ?? []).map((s) => (
          <div key={s.id} className="ac-sched">
            <div className="ac-sched__head"><strong>{s.name}</strong>
              {/* IMPACT AVANT MODIFICATION (§64) : combien de règles seraient touchées. */}
              <span className={`ac-chip ${s.usedBy > 0 ? 'ac-chip--group' : ''}`}>{s.usedBy > 0 ? `${s.usedBy} règle(s)` : 'Aucune règle'}</span>
              {s.isDefault ? <span className="ac-chip">par défaut</span> : null}{s.holidayGroup ? <span className="ac-chip ac-chip--group">Fériés : {s.holidayGroup}</span> : null}
              <span className="lc-panel__spacer" />
              {!s.isDefault ? (
                <span className="ac-sched__actions">
                  <ActionButton caps={caps} action="ACCESS_SCHEDULE_WRITE" label="Modifier" onRun={() => setEditing(s)} />
                  <ActionButton caps={caps} action="ACCESS_SCHEDULE_WRITE" label="Supprimer" onRun={() => void removeSchedule(s)} />
                </span>
              ) : <span className="lc-hint">Horaire système</span>}
            </div>
            <div className="ac-week">{JOURS.map(([k, lbl]) => <div key={k} className="ac-week__day"><span className="ac-week__lbl">{lbl}</span>{(s.weekly?.[k] ?? []).map((sl, i) => <span key={i} className="ac-week__slot">{sl.start_time}–{sl.end_time}</span>)}{(s.weekly?.[k] ?? []).length === 0 ? <span className="ac-week__none">—</span> : null}</div>)}</div>
          </div>
        ))}
      </section>
      <section className="ac-panel">
        <div className="ac-cov-head"><h2 className="lc-section-title">Jours fériés</h2>
          <ActionButton caps={caps} action="ACCESS_HOLIDAY_WRITE" label="Gérer les jours fériés" onRun={() => setHolidayOpen(true)} />
        </div>
        <div className="ac-chips">{(data?.holidayGroups ?? []).map((h) => <span key={h.id} className="ac-chip">{h.name} · {h.days} jour(s)</span>)}</div>
      </section>
      <section className="ac-panel">
        <h2 className="lc-section-title">Programmation de déverrouillage des portes</h2>
        <p className="lc-hint">Concept DISTINCT des horaires d’accès : ici la porte se déverrouille toute seule sur une plage (personne n’a besoin de badge). L’état en cours se lit sur chaque fiche porte — mode « Ouverture planifiée ».</p>
        <p className="ac-unavailable"><Lock size={14} aria-hidden="true" /> La création/modification de cette programmation n’est pas exposée par l’OpenAPI UniFi Access — elle se gère dans la console UniFi. Gestion ne l’invente pas et n’utilise aucun endpoint privé. Seul l’override temporaire officiel (« Forcer l’état », règle 7.10) est disponible sur la fiche porte.</p>
        <p className="lc-hint"><Link href="/securite/controle-acces/portes" className="ac-seeall">Voir l’état des portes →</Link></p>
      </section>
      {editing ? <ScheduleEditorModal schedule={editing === 'new' ? null : editing} onClose={(changed) => { setEditing(null); if (changed) setTimeout(reload, 1600) }} /> : null}
      {holidayOpen ? <HolidayModal groups={data?.holidayGroups ?? []} onClose={(changed) => { setHolidayOpen(false); if (changed) setTimeout(reload, 1600) }} /> : null}
    </AccessShell>
  )
}

/** ÉDITEUR D'HORAIRE (§113) — plages par jour, format officiel HH:MM:SS. */
function ScheduleEditorModal({ schedule, onClose }: { schedule: SchedRow | null; onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const [name, setName] = useState(schedule?.name ?? '')
  const [weekly, setWeekly] = useState<Record<string, { start_time: string; end_time: string }[]>>(() => {
    const w: Record<string, { start_time: string; end_time: string }[]> = {}
    for (const [k] of JOURS_FULL) w[k] = (schedule?.weekly?.[k] ?? []).map((x) => ({ ...x }))
    return w
  })
  const [busy, setBusy] = useState(false)
  const setSlot = (day: string, i: number, field: 'start_time' | 'end_time', v: string) => {
    const w = { ...weekly, [day]: weekly[day]!.map((x, j) => (j === i ? { ...x, [field]: v.length === 5 ? `${v}:00` : v } : x)) }
    setWeekly(w)
  }
  const addSlot = (day: string) => setWeekly({ ...weekly, [day]: [...weekly[day]!, { start_time: '10:00:00', end_time: '23:59:59' }] })
  const rmSlot = (day: string, i: number) => setWeekly({ ...weekly, [day]: weekly[day]!.filter((_, j) => j !== i) })
  const save = async () => {
    setBusy(true)
    const body = { name, weekly }
    const r = await runCommand('ACCESS_SCHEDULE_WRITE', schedule ? { schedule_id: schedule.id, schedule: body } : { schedule: body })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', schedule ? 'Horaire modifié dans UniFi Access.' : 'Horaire créé dans UniFi Access.')
    onClose(true)
  }
  return (
    <AccessModal title={schedule ? `Modifier — ${schedule.name}` : 'Nouvel horaire'} width={720} onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={!name || busy} onClick={() => void save()}>{busy ? 'Enregistrement…' : 'Enregistrer dans UniFi Access'}</button></>
    }>
      <label className="ac-field">Nom de l’horaire<input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. Soirs de week-end" /></label>
      <div className="ac-schededit">
        {JOURS_FULL.map(([k, lbl]) => (
          <div key={k} className="ac-schededit__day">
            <span className="ac-schededit__lbl">{lbl}</span>
            <div className="ac-schededit__slots">
              {weekly[k]!.map((slot, i) => (
                <span key={i} className="ac-schededit__slot">
                  <input type="time" value={slot.start_time.slice(0, 5)} onChange={(e) => setSlot(k, i, 'start_time', e.target.value)} aria-label={`${lbl} début`} />
                  –
                  <input type="time" value={slot.end_time.slice(0, 5)} onChange={(e) => setSlot(k, i, 'end_time', e.target.value)} aria-label={`${lbl} fin`} />
                  <button type="button" className="ac-inline-link" onClick={() => rmSlot(k, i)} aria-label={`Retirer la plage ${lbl}`}>×</button>
                </span>
              ))}
              <button type="button" className="ac-inline-link" onClick={() => addSlot(k)}>+ plage</button>
            </div>
          </div>
        ))}
      </div>
    </AccessModal>
  )
}

/** JOURS FÉRIÉS (§114) — création de groupe + jours (dates) ; suppression. */
function HolidayModal({ groups, onClose }: { groups: { id: string; name: string; days: number }[]; onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const confirm = useConfirm()
  const [name, setName] = useState('')
  const [days, setDays] = useState<{ name: string; date: string }[]>([{ name: '', date: '' }])
  const [busy, setBusy] = useState(false)
  const valid = name && days.some((d) => d.name && d.date)
  const create = async () => {
    setBusy(true)
    const holidays = days.filter((d) => d.name && d.date).map((d) => ({
      name: d.name, repeat: false,
      start_time: `${d.date} 00:00:00Z`, end_time: `${d.date} 23:59:59Z`,
    }))
    const r = await runCommand('ACCESS_HOLIDAY_WRITE', { holiday_group: { name, holidays } })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'Groupe de jours fériés créé dans UniFi Access.')
    onClose(true)
  }
  const remove = async (g: { id: string; name: string }) => {
    if (!(await confirm({ title: `Supprimer « ${g.name} » ?`, message: 'Le groupe de jours fériés est supprimé d’UniFi Access.', variant: 'sensitive', confirmLabel: 'Supprimer' }))) return
    const r = await runCommand('ACCESS_HOLIDAY_WRITE', { holiday_group_id: g.id, delete: true })
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'Groupe supprimé dans UniFi Access.')
    onClose(true)
  }
  return (
    <AccessModal title="Jours fériés" onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Fermer</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={!valid || busy} onClick={() => void create()}>{busy ? 'Création…' : 'Créer le groupe'}</button></>
    }>
      <h3 className="ac-subtitle">Groupes existants</h3>
      <ul className="ac-linklist">
        {groups.map((g) => (
          <li key={g.id}><span className="ac-linkrow"><strong>{g.name}</strong><em>{g.days} jour(s)</em><button type="button" className="ac-inline-link" onClick={() => void remove(g)}>Supprimer</button></span></li>
        ))}
        {groups.length === 0 ? <li className="lc-hint">Aucun groupe.</li> : null}
      </ul>
      <h3 className="ac-subtitle">Nouveau groupe</h3>
      <label className="ac-field">Nom du groupe<input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. Fériés France" /></label>
      {days.map((d, i) => (
        <div key={i} className="ac-holidayrow">
          <input placeholder="Nom du jour (ex. 14 juillet)" value={d.name} onChange={(e) => setDays(days.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
          <input type="date" value={d.date} onChange={(e) => setDays(days.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))} aria-label="Date du jour férié" />
        </div>
      ))}
      <button type="button" className="ac-inline-link" onClick={() => setDays([...days, { name: '', date: '' }])}>+ jour</button>
    </AccessModal>
  )
}

/* ═══════════════ INTERPHONIE ═══════════════ */
export function AccessIntercomPage() {
  const { data } = useApi<{ devices: { id: string; name: string; alias: string; type: string; online: boolean }[]; ringEvents: { at: string | null; actor: string | null; door: string | null; result: string | null; message: string | null }[]; directoryAvailable: boolean }>('/api/v1/access/intercom')
  return (
    <AccessShell title="Interphonie" subtitle="Les interphones du club et l’historique des appels.">
      <section className="ac-panel"><h2 className="lc-section-title">Appareils d’interphonie</h2>
        <div className="ac-device-grid">{(data?.devices ?? []).map((d) => <div key={d.id} className="ac-device-card">{d.online ? <Wifi size={16} className="ac-online" aria-hidden="true" /> : <WifiOff size={16} className="ac-offline" aria-hidden="true" />}<strong>{d.alias || d.name}</strong><span>{d.type}</span></div>)}</div>
      </section>
      <section className="ac-panel"><h2 className="lc-section-title">Annuaire d’interphonie</h2>
        <p className="ac-unavailable"><Lock size={14} aria-hidden="true" /> NON DISPONIBLE VIA L’API UniFi Access — la gestion de l’annuaire n’est pas exposée par la console. Gestion ne l’invente pas et n’utilise aucun endpoint privé.</p>
      </section>
      <section className="ac-panel"><h2 className="lc-section-title">Appels récents</h2>
        <ul className="ac-events">{(data?.ringEvents ?? []).map((e, i) => <li key={i} className="ac-event"><span className="ac-event__time">{e.at ? new Date(e.at).toLocaleString('fr-FR') : '—'}</span><span className="ac-event__door">{e.door ?? '—'}</span><span className="ac-event__result">{e.message ?? e.result}</span></li>)}{(data?.ringEvents ?? []).length === 0 ? <li className="lc-hint">Aucun appel enregistré.</li> : null}</ul>
      </section>
    </AccessShell>
  )
}

/* ═══════════════ HISTORIQUE (§17, §89-90) — filtres complets + URL ═══════════════ */

export function AccessHistoryPage() {
  const q0 = useMemo(() => new URLSearchParams(window.location.search), [])
  const [search, setSearch] = useState(q0.get('q') ?? '')
  const [result, setResult] = useState(q0.get('resultat') ?? '')
  const [identified, setIdentified] = useState(q0.get('identifie') === '1')
  const [period, setPeriod] = useState(q0.get('periode') ?? '')
  const [method, setMethod] = useState(q0.get('methode') ?? '')
  const [person] = useState(q0.get('personne') ?? '')
  const [door] = useState(q0.get('porte') ?? '')
  const menu = useMenuAcces()

  useEffect(() => {
    const p = new URLSearchParams()
    if (search) p.set('q', search)
    if (result) p.set('resultat', result)
    if (identified) p.set('identifie', '1')
    if (period) p.set('periode', period)
    if (method) p.set('methode', method)
    if (person) p.set('personne', person)
    if (door) p.set('porte', door)
    window.history.replaceState(null, '', `/securite/controle-acces/historique${p.size ? `?${p}` : ''}`)
  }, [search, result, identified, period, method, person, door])

  const api = new URLSearchParams()
  if (search) api.set('search', search)
  if (result) api.set('result', result)
  if (identified) api.set('identified', '1')
  if (period) api.set('period', period)
  if (method) api.set('method', method)
  if (person) api.set('actorId', person)
  if (door) api.set('doorId', door)
  type EvenementLigne = EvenementHistorique & { id: string; actorId: string | null; doorId: string | null; type: string | null }
  const { data, error } = useApi<{ events: EvenementLigne[]; total: number; night: NuitHistorique | null }>(`/api/v1/access/history${api.size ? `?${api}` : ''}`)
  const personName = person && data ? data.events.find((e) => e.actorId === person)?.actor ?? null : null
  const toast = useToast()
  const [exportEnCours, setExportEnCours] = useState(false)

  // EXPORT CSV (§120) — la période ENTIÈRE avec les filtres de la page, relue
  // par le serveur (au plus 5 000 événements, les plus récents), pas les 50
  // lignes affichées. Le serveur trace l'export ; le CSV se fabrique ici.
  const exportCsv = async () => {
    if (exportEnCours) return
    setExportEnCours(true)
    try {
      const r = await apiRead<{ events?: EvenementLigne[]; total?: number; truncated?: boolean; max?: number; night?: NuitHistorique | null; error?: string; message?: string }>(`/api/v1/access/history/export${api.size ? `?${api}` : ''}`, { headers: EN_TETES_EXPORT })
      if (r.status >= 400 || !Array.isArray(r.data.events)) {
        toast('error', r.data.message ?? 'L’export n’a pas pu être préparé. Réessayez.')
        return
      }
      const events = r.data.events
      const blob = new Blob([csvHistorique(events)], { type: 'text/csv;charset=utf-8' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = nomFichierHistorique(period, r.data.night ?? null)
      document.body.appendChild(a)
      a.click()
      a.remove()
      // Révoquée APRÈS le départ du téléchargement, comme admin/telechargement.ts : juste après click(), WebKit l'annulait.
      const adresse = a.href
      setTimeout(() => URL.revokeObjectURL(adresse), 1000)
      toast('ok', messageExport(events.length, r.data.total ?? events.length, r.data.truncated === true, r.data.max ?? events.length))
    } catch {
      toast('error', 'L’export n’a pas pu être préparé. Réessayez.')
    } finally {
      setExportEnCours(false)
    }
  }

  return (
    <AccessShell title="Historique" subtitle={`Tous les passages et événements d’accès${data ? ` — ${data.total} sur la période` : ''}${personName ? ` · ${personName}` : ''}.`}
      right={<button type="button" className="lc-btn" onClick={() => void exportCsv()} disabled={!data || data.events.length === 0 || exportEnCours}><Download size={14} aria-hidden="true" /> {exportEnCours ? 'Export…' : 'Exporter CSV'}</button>}>
      <div className="ac-filters">
        <input className="ac-search" placeholder="Rechercher (personne, porte, message)…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select value={result} options={[{ value: '', label: 'Tous résultats' }, { value: 'ACCESS', label: 'Accordés' }, { value: 'BLOCKED', label: 'Refusés' }]} onChange={setResult} ariaLabel="Résultat" />
        <Select value={period} options={[{ value: '', label: 'Toute la période' }, { value: 'soiree', label: 'Nuit en cours' }, { value: 'soiree-precedente', label: 'Nuit précédente' }, { value: '24h', label: '24 heures' }, { value: '7d', label: '7 jours' }, { value: '30d', label: '30 jours' }]} onChange={setPeriod} ariaLabel="Période" placeholder="Période inconnue" />
        <Select value={method} options={[{ value: '', label: 'Toutes méthodes' }, { value: 'FACE', label: 'Visage' }, { value: 'NFC', label: 'NFC' }, { value: 'PIN_CODE', label: 'PIN' }, { value: 'WALLET_NFC_IPHONE', label: 'iPhone' }, { value: 'WALLET_NFC_IWATCH', label: 'Watch' }]} onChange={setMethod} ariaLabel="Méthode" />
        <button type="button" className={`ac-qf ${identified ? 'is-on' : ''}`} aria-pressed={identified} onClick={() => setIdentified(!identified)}>Passages identifiés</button>
      </div>
      {data?.night ? <p className="lc-hint">{phraseNuitHistorique(data.night)}</p> : null}
      {error ? <p className="lc-hint ac-err">{error}</p> : null}
      <ul className="ac-events ac-events--full">
        {(data?.events ?? []).map((e) => {
          const inner = (
            <>
              <span className="ac-event__time">{dateTime(e.at)}</span>
              <span className="ac-event__door">{e.door ?? '—'}</span>
              <span className="ac-event__actor">{e.actor ?? 'Système'}</span>
              <span className="ac-event__method">{tr(METHOD_LABELS, e.method)}</span>
              <span className="ac-event__result">{e.result ? tr(RESULT_LABELS, e.result) : (e.message ?? e.type)}</span>
            </>
          )
          return (
            <li key={e.id} className={`ac-event ${e.result && e.result !== 'ACCESS' ? 'ac-event--denied' : ''} ${e.actorId ? 'ac-event--link' : ''}`}>
              {e.actorId ? <Link href={`/securite/controle-acces/personnes/${e.actorId}`} className="ac-event__wrap" {...menu.personne({ id: e.actorId, nom: e.actor })}>{inner}</Link> : inner}
            </li>
          )
        })}
        {data && data.events.length === 0 ? <li className="lc-hint">{data.night ? 'Aucun événement sur cette nuit.' : 'Aucun événement.'}</li> : null}
      </ul>
    </AccessShell>
  )
}

/* ═══════════════ APPAREILS V2.1 (§117-131) — méthodes réelles + diagnostic ═══════════════ */

interface DeviceMethod { key: string; enabled: boolean; extra: Record<string, unknown> }
interface DeviceRow {
  id: string; name: string; alias: string; type: string; online: boolean; adopted: boolean; managed: boolean
  hubId: string | null; doorId: string | null; doorName: string | null
  isReader: boolean; enrollCapable: boolean; accessMethods: DeviceMethod[]
  diagnostic: { providerId: string; type: string | null; capabilities: string[]; rawAccessMethods: unknown; lastSync: string | null }
}

const ACCESS_METHOD_LABELS: Record<string, string> = {
  nfc: 'NFC', pin_code: 'Code PIN', face: 'Visage', qr_code: 'Code QR', touch_pass: 'Touch Pass',
  bt_tap: 'Bluetooth Tap', bt_button: 'Bouton Bluetooth', bt_shake: 'Bluetooth Shake',
  mobile_wave: 'Mobile Wave', hand_wave: 'Hand Wave', wave: 'Wave', mobile_tap: 'Mobile Tap', apple_wallet: 'Apple Wallet',
}

export function AccessDevicesPage() {
  const q0 = useMemo(() => new URLSearchParams(window.location.search), [])
  const [filter, setFilter] = useState(q0.get('etat') ?? 'tous')
  const [editing, setEditing] = useState<DeviceRow | null>(null)
  const caps = useAccessCaps()
  const menu = useMenuAcces()
  const { data, reload } = useApi<{ devices: DeviceRow[] }>('/api/v1/access/devices')
  useEffect(() => {
    window.history.replaceState(null, '', `/securite/controle-acces/appareils${filter !== 'tous' ? `?etat=${filter}` : ''}`)
  }, [filter])
  const shown = (data?.devices ?? []).filter((d) => filter !== 'hors-ligne' || !d.online)
  return (
    <AccessShell title="Appareils" subtitle="Hubs, lecteurs et interphones — méthodes d’accès réelles par appareil.">
      <div className="ac-quickfilters" role="tablist" aria-label="Filtres appareils">
        {[['tous', 'Tous'], ['hors-ligne', 'Hors ligne']].map(([v, l]) => (
          <button key={v} type="button" role="tab" aria-selected={filter === v} className={`ac-qf ${filter === v ? 'is-on' : ''}`} onClick={() => setFilter(v!)}>{l}</button>
        ))}
      </div>
      <div className="ac-device-grid">
        {shown.map((d) => {
          const on = d.accessMethods.filter((m) => m.enabled)
          return (
            <div key={d.id} className="ac-device-card">
              {d.online ? <Wifi size={16} className="ac-online" aria-hidden="true" /> : <WifiOff size={16} className="ac-offline" aria-hidden="true" />}
              <strong>{d.alias || d.name}</strong>
              <span>{d.type}</span>
              {/* Relation RÉELLE appareil → porte (location_id), navigable (§115). */}
              {d.doorId ? <Link href={`/securite/controle-acces/portes/${d.doorId}`} className="ac-device-card__door" {...menu.porte({ id: d.doorId, nom: d.doorName })}>Porte : {d.doorName ?? d.doorId}</Link> : <span className="lc-hint">Aucune porte associée</span>}
              <span className="ac-device-card__status">{d.online ? 'En ligne' : 'Hors ligne'}{d.adopted ? '' : ' · non adopté'}{d.enrollCapable ? ' · enrôlement' : ''}</span>
              {d.accessMethods.length > 0 ? (
                <span className="ac-device-card__methods">{on.length > 0 ? on.map((m) => tr(ACCESS_METHOD_LABELS, m.key)).join(' · ') : 'Toutes méthodes désactivées'}</span>
              ) : <span className="lc-hint">Pas de méthodes d’accès (hub de porte)</span>}
              <ActionButton caps={caps} action="ACCESS_DEVICE_WRITE" label={d.accessMethods.length > 0 ? 'Méthodes d’accès' : 'Diagnostic'} icon={<Settings2 size={13} aria-hidden="true" />} onRun={() => setEditing(d)} />
            </div>
          )
        })}
        {data && shown.length === 0 ? <p className="lc-hint">{filter === 'hors-ligne' ? 'Aucun appareil hors ligne — tout est en ligne.' : 'Aucun appareil.'}</p> : null}
      </div>
      {editing ? <DeviceSettingsModal device={editing} onClose={(changed) => { setEditing(null); if (changed) setTimeout(reload, 1600) }} /> : null}
    </AccessShell>
  )
}

/** MÉTHODES D'ACCÈS (V2.1 §117-119) — le format RÉEL du provider est
 *  settings.access_methods.<clé>.enabled = "yes"/"no" (vérifié en live sur la
 *  console 4.3.7) : l'ancien mapping plat "true"/"false" issu du PDF était la
 *  cause du « Aucune méthode exposée » mensonger. Diagnostic intégré (§129). */
function DeviceSettingsModal({ device, onClose }: { device: DeviceRow; onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const [diagOpen, setDiagOpen] = useState(device.accessMethods.length === 0)
  const initial = useMemo(() => Object.fromEntries(device.accessMethods.map((m) => [m.key, m.enabled])), [device])
  const [state, setState] = useState<Record<string, boolean>>(initial)
  const [busy, setBusy] = useState(false)
  const dirty = device.accessMethods.some((m) => state[m.key] !== m.enabled)
  const save = async () => {
    setBusy(true)
    // N'envoyer QUE les méthodes modifiées, au format officiel nested yes/no.
    const access_methods: Record<string, { enabled: string }> = {}
    for (const m of device.accessMethods) {
      if (state[m.key] !== m.enabled) access_methods[m.key] = { enabled: state[m.key] ? 'yes' : 'no' }
    }
    const r = await runCommand('ACCESS_DEVICE_WRITE', { device_id: device.id, settings: { access_methods } })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', `Méthodes d’accès de ${device.alias || device.name} modifiées dans UniFi Access.`)
    onClose(true)
  }
  return (
    <AccessModal title={`Méthodes d’accès — ${device.alias || device.name}`} onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={!dirty || busy} onClick={() => void save()}>{busy ? 'Enregistrement…' : 'Enregistrer dans UniFi Access'}</button></>
    }>
      <p className="lc-hint">Active ou désactive les méthodes d’accès SUR CET APPAREIL. Réglages officiels UniFi Access.</p>
      <div className="ac-checkset">
        {device.accessMethods.map((m) => (
          <label key={m.key} className={`ac-radiorow ${state[m.key] ? 'is-on' : ''}`}>
            <input type="checkbox" checked={state[m.key] ?? false} onChange={(e) => setState({ ...state, [m.key]: e.target.checked })} />{tr(ACCESS_METHOD_LABELS, m.key)}
          </label>
        ))}
        {device.accessMethods.length === 0 ? (
          <p className="lc-hint">Cet appareil ({device.type}) n’expose aucune méthode d’accès dans ses réglages provider — c’est un hub de porte, pas un lecteur. Le détail brut est dans le diagnostic ci-dessous.</p>
        ) : null}
      </div>
      {/* DIAGNOSTIC APPAREIL (§129) — le brut du provider, pour trancher API vs UI. */}
      <button type="button" className="ac-inline-link" onClick={() => setDiagOpen(!diagOpen)}>{diagOpen ? 'Masquer' : 'Afficher'} le diagnostic API</button>
      {diagOpen ? (
        <dl className="ac-dl ac-dl--diag">
          <div><dt>Provider ID</dt><dd className="ac-mono">{device.diagnostic.providerId}</dd></div>
          <div><dt>Type</dt><dd className="ac-mono">{device.diagnostic.type ?? '—'}</dd></div>
          <div><dt>Capabilities</dt><dd className="ac-mono">{device.diagnostic.capabilities.join(', ') || '—'}</dd></div>
          <div><dt>access_methods (brut)</dt><dd className="ac-mono ac-mono--wrap">{JSON.stringify(device.diagnostic.rawAccessMethods) ?? 'null'}</dd></div>
          <div><dt>Dernière sync</dt><dd>{device.diagnostic.lastSync ? new Date(device.diagnostic.lastSync).toLocaleString('fr-FR') : '—'}</dd></div>
        </dl>
      ) : null}
    </AccessModal>
  )
}

/* ═══════════════ URGENCE V2 (§121-123) — réelle, hyper verrouillée ═══════════════ */
export function AccessEmergencyPage() {
  const caps = useAccessCaps()
  const toast = useToast()
  const [confirming, setConfirming] = useState<'LOCKDOWN' | 'EVACUATION' | 'RELEASE' | null>(null)
  const { data, reload } = useApi<{ state: 'NORMAL' | 'LOCKDOWN' | 'EVACUATION'; evacuation: boolean; lockdown: boolean; doorCount: number; canManage: boolean; editable: boolean }>('/api/v1/access/emergency')
  return (
    <AccessShell title="Urgence" subtitle="Confinement et évacuation — l’état réel, et son déclenchement.">
      {data ? (
        <div className={`ac-emergency ac-emergency--${data.state.toLowerCase()}`}>
          <div className="ac-emergency__state">
            {data.state === 'NORMAL' ? <CircleCheck size={40} aria-hidden="true" /> : <ShieldAlert size={40} aria-hidden="true" />}
            <div><span className="ac-emergency__label">État actuel</span><strong>{data.state === 'NORMAL' ? 'Normal' : data.state === 'LOCKDOWN' ? 'Confinement' : 'Évacuation'}</strong><span>{data.doorCount} porte(s) concernée(s)</span></div>
          </div>
          <div className="ac-emergency__actions">
            {data.state === 'NORMAL' ? (<>
              <ActionButton caps={caps} action="ACCESS_EMERGENCY" label="Déclencher un confinement" icon={<Lock size={14} aria-hidden="true" />} onRun={() => setConfirming('LOCKDOWN')} />
              <ActionButton caps={caps} action="ACCESS_EMERGENCY" label="Déclencher une évacuation" icon={<ShieldAlert size={14} aria-hidden="true" />} onRun={() => setConfirming('EVACUATION')} />
            </>) : (
              <ActionButton caps={caps} action="ACCESS_EMERGENCY" label="Lever l’état d’urgence" primary onRun={() => setConfirming('RELEASE')} />
            )}
          </div>
          <p className="ac-cover-note">Action HYPER critique : permission « Gérer l’urgence », step-up (même Super Admin) et confirmation détaillée. TOUTES les portes du site changent d’état immédiatement. Jamais testé en production par Gestion.</p>
          {confirming ? <EmergencyConfirmModal kind={confirming} doorCount={data.doorCount} onClose={(changed) => { setConfirming(null); if (changed) { toast('ok', 'État d’urgence appliqué par UniFi Access.'); setTimeout(reload, 1600) } }} /> : null}
        </div>
      ) : <p className="lc-hint">Chargement…</p>}
    </AccessShell>
  )
}

function EmergencyConfirmModal({ kind, doorCount, onClose }: { kind: 'LOCKDOWN' | 'EVACUATION' | 'RELEASE'; doorCount: number; onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const [ack, setAck] = useState(false)
  const [busy, setBusy] = useState(false)
  const LABELS = { LOCKDOWN: 'CONFINEMENT — toutes les portes se VERROUILLENT', EVACUATION: 'ÉVACUATION — toutes les portes se DÉVERROUILLENT', RELEASE: 'LEVÉE — retour à l’état normal' }
  const run = async () => {
    setBusy(true)
    const r = await runCommand('ACCESS_EMERGENCY', { lockdown: kind === 'LOCKDOWN', evacuation: kind === 'EVACUATION' })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    onClose(true)
  }
  return (
    <AccessModal title="Confirmation d’urgence" onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={!ack || busy} onClick={() => void run()}>{busy ? 'Application…' : 'Appliquer MAINTENANT'}</button></>
    }>
      <p className="ac-emergency__warn"><ShieldAlert size={16} aria-hidden="true" /> <strong>{LABELS[kind]}</strong></p>
      <p className="lc-hint">{doorCount} porte(s) du site sont concernées, immédiatement et physiquement. Cette action est auditée et exige une ré-authentification forte.</p>
      <label className="ac-radiorow"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> Je comprends les conséquences physiques immédiates de cette action.</label>
    </AccessModal>
  )
}

/* ═══════════════ GROUPES D'UTILISATEURS (§105) — CRUD + membres ═══════════════ */

interface GroupRow { id: string; name: string | null; memberCount: number }

export function AccessGroupsPage() {
  const caps = useAccessCaps()
  const toast = useToast()
  const confirm = useConfirm()
  const [createOpen, setCreateOpen] = useState(false)
  const [renaming, setRenaming] = useState<GroupRow | null>(null)
  const [members, setMembers] = useState<GroupRow | null>(null)
  const { data, reload } = useApi<{ groups: GroupRow[] }>('/api/v1/access/groups')

  const remove = async (g: GroupRow) => {
    if (!(await confirm({ title: `Supprimer le groupe « ${g.name} » ?`, message: `Ses ${g.memberCount} membre(s) ne sont pas supprimés — seulement le groupe.`, variant: 'sensitive', confirmLabel: 'Supprimer' }))) return
    const r = await runCommand('ACCESS_GROUP_WRITE', { group_id: g.id, delete: true })
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'Groupe supprimé dans UniFi Access.')
    setTimeout(reload, 1600)
  }

  return (
    <AccessShell title="Groupes d’utilisateurs" subtitle="Les groupes UniFi Access — création, renommage, membres." leaf="Groupes"
      right={<ActionButton caps={caps} action="ACCESS_GROUP_WRITE" label="Nouveau groupe" icon={<Plus size={14} aria-hidden="true" />} onRun={() => setCreateOpen(true)} />}>
      <section className="lc-panel ac-plist-panel">
        <div className="lc-panel__body lc-panel__body--flush">
          <ul className="ac-plist">
            {(data?.groups ?? []).map((g) => (
              <li key={g.id}>
                <div className="ac-prow ac-grouprow">
                  <span className="ac-prow__id"><Users size={16} aria-hidden="true" className="ac-credicon" />
                    <span className="ac-prow__name"><strong>{g.name}</strong><em>{g.memberCount} membre(s)</em></span>
                  </span>
                  <span className="ac-grouprow__actions">
                    <Link href={`/securite/controle-acces/personnes?groupe=${g.id}`} className="lc-btn lc-btn--ghost ac-btn-sm">Voir les membres</Link>
                    <ActionButton caps={caps} action="ASSIGN_GROUP" label="Gérer les membres" onRun={() => setMembers(g)} />
                    <ActionButton caps={caps} action="ACCESS_GROUP_WRITE" label="Renommer" onRun={() => setRenaming(g)} />
                    <ActionButton caps={caps} action="ACCESS_GROUP_WRITE" label="Supprimer" onRun={() => void remove(g)} />
                  </span>
                </div>
              </li>
            ))}
          </ul>
          {data && data.groups.length === 0 ? <p className="lc-hint" style={{ padding: 'var(--sp-3) var(--sp-6)' }}>Aucun groupe.</p> : null}
        </div>
      </section>
      {createOpen || renaming ? (
        <GroupNameModal group={renaming} onClose={(changed) => { setCreateOpen(false); setRenaming(null); if (changed) setTimeout(reload, 1600) }} />
      ) : null}
      {members ? <GroupMembersModal group={members} onClose={(changed) => { setMembers(null); if (changed) setTimeout(reload, 1600) }} /> : null}
    </AccessShell>
  )
}

function GroupNameModal({ group, onClose }: { group: GroupRow | null; onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const [name, setName] = useState(group?.name ?? '')
  const [busy, setBusy] = useState(false)
  const save = async () => {
    setBusy(true)
    const r = await runCommand('ACCESS_GROUP_WRITE', group ? { group_id: group.id, name } : { name })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', group ? 'Groupe renommé dans UniFi Access.' : 'Groupe créé dans UniFi Access.')
    onClose(true)
  }
  return (
    <AccessModal title={group ? `Renommer — ${group.name}` : 'Nouveau groupe'} onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={!name.trim() || busy} onClick={() => void save()}>{busy ? 'Enregistrement…' : 'Enregistrer dans UniFi Access'}</button></>
    }>
      <label className="ac-field">Nom du groupe<input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex. Équipe bar" /></label>
    </AccessModal>
  )
}

/** MEMBRES (§3.16/3.17) — ajouts et retraits par lots officiels. */
function GroupMembersModal({ group, onClose }: { group: GroupRow; onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const { data } = useApi<{ people: { id: string; fullName: string; groups: { id: string }[] }[] }>('/api/v1/access/people')
  const initial = useMemo(() => new Set((data?.people ?? []).filter((p) => p.groups.some((g) => g.id === group.id)).map((p) => p.id)), [data, group.id])
  const [sel, setSel] = useState<Set<string> | null>(null)
  useEffect(() => { if (data && sel === null) setSel(new Set(initial)) }, [data, initial, sel])
  const [busy, setBusy] = useState(false)
  const toggle = (id: string) => { const n = new Set(sel!); if (n.has(id)) n.delete(id); else n.add(id); setSel(n) }
  const added = sel ? [...sel].filter((id) => !initial.has(id)) : []
  const removed = sel ? [...initial].filter((id) => !sel.has(id)) : []
  const save = async () => {
    setBusy(true)
    if (added.length) {
      const r = await runCommand('ASSIGN_GROUP', { group_id: group.id, user_ids: added })
      if (!r.ok) { setBusy(false); toast('error', r.error ?? 'Refusé.'); return }
    }
    if (removed.length) {
      const r = await runCommand('UNASSIGN_GROUP', { group_id: group.id, user_ids: removed })
      if (!r.ok) { setBusy(false); toast('error', r.error ?? 'Refusé.'); return }
    }
    setBusy(false)
    toast('ok', 'Membres du groupe mis à jour dans UniFi Access.')
    onClose(true)
  }
  return (
    <AccessModal title={`Membres — ${group.name}`} onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={busy || (added.length === 0 && removed.length === 0)} onClick={() => void save()}>{busy ? 'Enregistrement…' : `Enregistrer (${added.length} ajout(s), ${removed.length} retrait(s))`}</button></>
    }>
      <ul className="ac-pollist" style={{ maxHeight: 340 }}>
        {(data?.people ?? []).map((p) => (
          <li key={p.id}>
            <label className={`ac-polrow ${sel?.has(p.id) ? 'is-on' : ''}`}>
              <input type="checkbox" checked={sel?.has(p.id) ?? false} onChange={() => toggle(p.id)} />
              <span className="ac-polrow__name"><strong>{p.fullName}</strong></span>
            </label>
          </li>
        ))}
      </ul>
    </AccessModal>
  )
}
